// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing › New run — run the passage-planning engine (vortex-routing) on the live
// forecasts: one route, a comparison of models for one departure, or a departure window. Jobs
// run on the routing service; this page submits one through the API and follows it. Every run
// is listed, and can be stopped, restarted or deleted, on Admin › Routing (AdminRoutingPage).

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CircleMarker, Polyline, Tooltip } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { AdminFleetMap } from '../../components/admin/console/AdminFleetMap';
import { formatLocal } from './weatherFormat';
import { Card, Pill } from './routingUi';
import { RoutePicker, type Passage, type PickMode } from './RoutePicker';
import { RoutingBoat, loadSavedPolar } from './RoutingBoat';
import { BASEMAPS, PRESETS, basemapById } from './routingMap';
import { durationText, modelName, routeLine, statusView, windowRows, type Tone } from './routingFormat';
import {
  api,
  type RoutingDeparture,
  type LatLon,
  type RoutingForm,
  type RoutingJobStatus,
  type RoutingKind,
  type RoutingModel,
  type RoutingPolar,
  type RoutingResult,
  type RoutingRoute,
  type RoutingSearch,
  type RoutingValidation,
} from '../../services/api';

const MODELS: { id: RoutingModel; name: string; reach: string }[] = [
  { id: 'ecmwf0p25', name: 'ECMWF', reach: '15 days' },
  { id: 'gfs025', name: 'GFS', reach: '10 days' },
  { id: 'icon025', name: 'ICON', reach: '7½ days' },
];

const KINDS: { id: RoutingKind; name: string; hint: string }[] = [
  { id: 'route', name: 'One route', hint: 'The best route for one departure, on the first model ticked, with its safety checks.' },
  { id: 'evaluate', name: 'Compare models', hint: 'One departure under every model ticked: each model’s route, re-sailed under the others, and the most robust.' },
  { id: 'window', name: 'Departure window', hint: 'Many departure times under every model ticked, to find when to leave.' },
];

const PRESET = PRESETS[0];
const fmt = (x: number) => String(x);

/** The basemap chosen on the passage map, used by the result maps too. */
const BasemapContext = createContext(BASEMAPS[0].id);

interface FormState {
  kind: RoutingKind;
  label: string;
  startLat: string;
  startLon: string;
  endLat: string;
  endLon: string;
  via: LatLon[];
  depart: string; // datetime-local, the viewer's zone
  windowDays: number;
  intervalH: number;
  models: Record<RoutingModel, boolean>;
  resolution: 'quick' | 'standard' | 'fine';
  maxTws: string;
  maxHs: string;
  maxGust: string;
  motoring: boolean;
  motoringKt: string;
  currents: boolean;
}

function nextHourLocal(): string {
  const d = new Date(Math.ceil(Date.now() / 3_600_000) * 3_600_000 + 3_600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:00`;
}

const DEFAULTS = (): FormState => ({
  kind: 'route', label: '', startLat: fmt(PRESET.start[0]), startLon: fmt(PRESET.start[1]), endLat: fmt(PRESET.end[0]), endLon: fmt(PRESET.end[1]), via: [],
  depart: nextHourLocal(), windowDays: 3, intervalH: 6, models: { ecmwf0p25: true, gfs025: false, icon025: false },
  resolution: 'quick', maxTws: '25', maxHs: '3.5', maxGust: '', motoring: true, motoringKt: '5.5', currents: false,
});

/** The form as the API takes it, or why it cannot be sent. */
export function toRoutingForm(f: FormState, polar: RoutingPolar | null = null): RoutingForm | string {
  const n = (s: string) => (s.trim() === '' ? NaN : Number(s));
  const opt = (s: string) => (s.trim() === '' ? null : Number(s));
  const start: [number, number] = [n(f.startLat), n(f.startLon)];
  const end: [number, number] = [n(f.endLat), n(f.endLon)];
  if (![...start, ...end].every(Number.isFinite)) return 'Enter the start and end as decimal degrees (south and west negative).';
  const models = MODELS.map((m) => m.id).filter((id) => f.models[id]);
  if (!models.length) return 'Tick at least one model.';
  const departMs = new Date(f.depart).getTime();
  if (!Number.isFinite(departMs)) return 'Choose a departure time.';
  const form: RoutingForm = {
    kind: f.kind, start, end, models, resolution: f.resolution, currents: f.currents ? 'forecast' : 'zero',
    limits: { maxTwsKt: n(f.maxTws), maxGustKt: opt(f.maxGust), maxHsM: opt(f.maxHs) },
    motoring: { enabled: f.motoring, speedKt: n(f.motoringKt) || 5 },
    ...(f.label.trim() ? { label: f.label.trim() } : {}),
    ...(f.via.length ? { via: f.via } : {}),
    ...(polar ? { polar } : {}),
  };
  if (f.kind === 'window') form.window = { fromMs: departMs, toMs: departMs + f.windowDays * 86_400_000, intervalH: f.intervalH };
  else form.departMs = departMs;
  return form;
}

const input = 'w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-[14px] text-white focus:border-cyan-500 focus:outline-none';
const label = 'block text-[13px] font-semibold text-slate-300 mb-1';

function Field({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={label}>{name}</span>
      {children}
    </label>
  );
}

const JOB_TONE: Record<string, Tone> = { queued: 'info', running: 'info', done: 'ok', failed: 'err' };
const KIND_NAME: Record<string, string> = { route: 'Route', evaluate: 'Compare models', window: 'Departure window' };

function RouteMap({ routes }: { routes: { route: RoutingRoute; colour: string; name: string }[] }) {
  const base = basemapById(useContext(BasemapContext));
  const lines = routes.map((r) => ({ ...r, line: routeLine(r.route) })).filter((r) => r.line.length > 1);
  if (!lines.length) return null;
  const all = lines.flatMap((l) => l.line);
  const lat = all.reduce((s, p) => s + p[0], 0) / all.length;
  const lon = all.reduce((s, p) => s + p[1], 0) / all.length;
  return (
    <div className="h-[420px] rounded-lg overflow-hidden border border-slate-700">
      <AdminFleetMap center={[lat, lon]} zoom={5} tileUrl={base.url} tileSource={base.source} tileAttribution={base.attribution} tileMaxZoom={base.maxZoom}>
        {lines.map((l) => (
          <Polyline key={l.name} positions={l.line} pathOptions={{ color: l.colour, weight: 3 }}>
            <Tooltip sticky>{l.name}</Tooltip>
          </Polyline>
        ))}
        <CircleMarker center={lines[0].line[0]} radius={6} pathOptions={{ color: '#22d3ee', fillOpacity: 1 }}><Tooltip>Start</Tooltip></CircleMarker>
        <CircleMarker center={lines[0].line[lines[0].line.length - 1]} radius={6} pathOptions={{ color: '#f472b6', fillOpacity: 1 }}><Tooltip>Destination</Tooltip></CircleMarker>
      </AdminFleetMap>
    </div>
  );
}

function Stat({ name, value }: { name: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2">
      <p className="text-[13px] text-slate-400">{name}</p>
      <p className="text-[16px] font-semibold text-white">{value}</p>
    </div>
  );
}

const CHECK_NAME: Record<string, string> = { shoreline: 'Clear of land', depth_hazards: 'Depth and hazards', exclusions: 'No-go areas', weather_limits: 'Within the weather limits' };
const CHECK_TONE: Record<string, Tone> = { PASS: 'ok', FAIL: 'err', NOT_EVALUATED: 'mute', INSUFFICIENT_DATA: 'warn', DISCREPANCY: 'warn' };

function RouteResult({ search, validation }: { search: RoutingSearch; validation: RoutingValidation | null }) {
  const s = statusView(search.status);
  const r = search.route;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Pill tone={s.tone}>{s.text}</Pill>
        {!r && <p className="text-[14px] text-slate-300">{search.message}</p>}
      </div>
      {r && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat name="Leaves" value={formatLocal(r.departMs)} />
            <Stat name="Arrives" value={formatLocal(r.arriveMs)} />
            <Stat name="Passage" value={`${durationText(r.arriveMs - r.departMs)} · ${Math.round(r.distanceNm)} nm`} />
            <Stat name="Motoring · tacks · gybes" value={`${r.motoringHours.toFixed(1)} h · ${r.tacks} · ${r.gybes}`} />
          </div>
          {validation && (
            <div className="flex gap-2 flex-wrap">
              {validation.checks.map((c) => (
                <Pill key={c.id} tone={CHECK_TONE[c.state] ?? 'mute'}>
                  {CHECK_NAME[c.id] ?? c.id}: {c.state === 'PASS' ? 'yes' : c.state === 'NOT_EVALUATED' ? 'none drawn' : c.state === 'INSUFFICIENT_DATA' ? 'no data' : c.state.toLowerCase()}
                </Pill>
              ))}
            </div>
          )}
          <RouteMap routes={[{ route: r, colour: '#22d3ee', name: modelName(r.forecastId) }, ...search.alternatives.map((a, i) => ({ route: a, colour: '#94a3b8', name: `Alternative ${i + 1}` }))]} />
        </>
      )}
    </div>
  );
}

const MODEL_COLOUR: Record<string, string> = { ECMWF: '#22d3ee', GFS: '#fbbf24', ICON: '#a78bfa' };

function EvaluateResult({ d }: { d: RoutingDeparture }) {
  const best = d.compromise == null ? null : d.candidates[d.compromise];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {best ? <Pill tone="ok">Most robust: {modelName(best.fromForecast)}’s route, worst case {durationText((best.worstArriveMs ?? 0) - d.departMs)}</Pill> : <Pill tone="warn">No route works under every model</Pill>}
        <span className="text-[13px] text-slate-400">Leaves {formatLocal(d.departMs)}</span>
      </div>
      <table className="w-full text-[14px]">
        <thead>
          <tr className="text-left text-slate-400 text-[13px]">
            <th className="py-2 pr-4">Model</th>
            <th className="py-2 pr-4">Its own route</th>
            <th className="py-2 pr-4">That route under every model</th>
          </tr>
        </thead>
        <tbody>
          {d.runs.map((run) => {
            const c = d.candidates.find((x) => x.fromForecast === run.forecastId && x.rank === 0);
            const s = statusView(run.search.status);
            return (
              <tr key={run.forecastId} className="border-t border-slate-700 align-top">
                <td className="py-2 pr-4 font-semibold text-white">{modelName(run.forecastId)}</td>
                <td className="py-2 pr-4">
                  <Pill tone={s.tone}>{run.search.route ? durationText(run.search.route.arriveMs - run.search.route.departMs) : s.text}</Pill>
                  {run.cause !== 'none' && <p className="text-[13px] text-slate-400 mt-1">{run.causeDetail}</p>}
                </td>
                <td className="py-2 pr-4">
                  {c ? (
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(c.replays).map(([fid, rp]) => (
                        <Pill key={fid} tone={rp.feasible ? 'ok' : c.shortOf.includes(fid) ? 'mute' : 'warn'}>
                          {modelName(fid)}: {rp.feasible && rp.route ? durationText(rp.route.arriveMs - d.departMs) : c.shortOf.includes(fid) ? 'does not reach' : 'fails'}
                        </Pill>
                      ))}
                    </div>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <RouteMap routes={d.candidates.filter((c) => c.rank === 0).map((c) => ({ route: c.route, colour: MODEL_COLOUR[modelName(c.fromForecast)] ?? '#94a3b8', name: modelName(c.fromForecast) }))} />
    </div>
  );
}

function WindowResult({ departures }: { departures: RoutingDeparture[] }) {
  const { rows, best } = useMemo(() => windowRows(departures), [departures]);
  const [open, setOpen] = useState<number | null>(best);
  return (
    <div className="space-y-4">
      {best != null ? (
        <Pill tone="ok">Best departure: {formatLocal(rows[best].departMs)} — {durationText((rows[best].worstHours ?? 0) * 3_600_000)} worst case ({rows[best].bestModel}’s route)</Pill>
      ) : (
        <Pill tone="warn">No departure in this window works under every model</Pill>
      )}
      <table className="w-full text-[14px]">
        <thead>
          <tr className="text-left text-slate-400 text-[13px]">
            <th className="py-2 pr-4">Departure</th>
            <th className="py-2 pr-4">Worst case across the models</th>
            <th className="py-2 pr-4">Why not</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.departMs} onClick={() => setOpen(i)} className={`border-t border-slate-700 cursor-pointer ${i === open ? 'bg-slate-700/40' : 'hover:bg-slate-700/20'}`}>
              <td className="py-2 pr-4 text-white whitespace-nowrap">{formatLocal(r.departMs)} {i === best && <Pill tone="ok">best</Pill>}</td>
              <td className="py-2 pr-4">{r.worstHours != null ? `${durationText(r.worstHours * 3_600_000)} (${r.bestModel}’s route)` : '—'}</td>
              <td className="py-2 pr-4 text-[13px] text-slate-400">{r.why}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open != null && departures[open] && <EvaluateResult d={departures[open]} />}
    </div>
  );
}

/** A finished run's answer: its forecasts, then the route, comparison or window. Admin › Routing shows it on request. */
export function ResultView({ r }: { r: RoutingResult }) {
  const body = r.result as Record<string, unknown>;
  return (
    <div className="space-y-4">
      <p className="text-[13px] text-slate-400">
        Forecasts: {r.forecasts.map((f) => `${modelName(f.model)} ${f.runId}`).join(', ')} · Coastline {r.shoreline} · computed in {(r.computeMs / 1000).toFixed(1)} s · {r.engineVersion}
      </p>
      {'search' in body && <RouteResult search={body.search as RoutingSearch} validation={(body.validation as RoutingValidation) ?? null} />}
      {'departures' in body && <WindowResult departures={body.departures as RoutingDeparture[]} />}
      {'runs' in body && <EvaluateResult d={body as unknown as RoutingDeparture} />}
    </div>
  );
}

export function AdminRoutingNewRunPage() {
  const [f, setF] = useState<FormState>(DEFAULTS);
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState<RoutingJobStatus | null>(null);
  const [result, setResult] = useState<RoutingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState<PickMode>(null);
  const [basemap, setBasemap] = useState(BASEMAPS[0].id);
  const [seamarks, setSeamarks] = useState(false);
  const [polar, setPolar] = useState<RoutingPolar | null>(loadSavedPolar);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((x) => ({ ...x, [k]: v }));

  // The map and the coordinate boxes show the same passage; a box that does not parse leaves its point off the map.
  const point = (lat: string, lon: string): LatLon | null => {
    const p: LatLon = [Number(lat), Number(lon)];
    return lat.trim() && lon.trim() && p.every(Number.isFinite) ? p : null;
  };
  const passage: Passage = { start: point(f.startLat, f.startLon), end: point(f.endLat, f.endLon), via: f.via };
  const setPassage = (p: Passage) =>
    setF((x) => ({
      ...x,
      via: p.via,
      ...(p.start ? { startLat: fmt(p.start[0]), startLon: fmt(p.start[1]) } : {}),
      ...(p.end ? { endLat: fmt(p.end[0]), endLon: fmt(p.end[1]) } : {}),
    }));

  // Follow the selected job until it finishes, then fetch its result.
  useEffect(() => {
    if (!selected) return;
    let stop = false;
    setResult(null);
    const tick = async () => {
      try {
        const s = await api.adminRoutingStatus(selected);
        if (stop) return;
        setStatus(s);
        if (s.state === 'done') {
          const r = await api.adminRoutingResult(selected);
          if (!stop) setResult(r);
          return;
        }
        if (s.state === 'failed') return;
      } catch (e) {
        if (!stop) setError((e as Error).message);
        return;
      }
      if (!stop) setTimeout(tick, 3_000);
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [selected]);

  async function submit() {
    setError(null);
    const form = toRoutingForm(f, polar);
    if (typeof form === 'string') return setError(form);
    setBusy(true);
    try {
      const s = await api.adminRoutingSubmit(form);
      setStatus(s);
      setSelected(s.id);
    } catch (e) {
      const data = (e as { response?: { data?: { error?: string; message?: string } } }).response?.data;
      setError(data?.error ?? data?.message ?? (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const kind = KINDS.find((k) => k.id === f.kind)!;
  return (
    <BasemapContext.Provider value={basemap}>
    <div className="space-y-6">
      <div>
        <Link to="/admin/routing" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-cyan-300 hover:text-cyan-200">
          <ArrowLeftIcon className="w-4 h-4" /> Routing
        </Link>
        <h1 className="text-2xl font-bold text-white mt-2">New run</h1>
        <p className="text-[14px] text-slate-400 mt-1">
          Run the passage-planning engine on the live forecasts, with your boat’s polar. Planning output only — check every route against official charts.
        </p>
      </div>

      <Card title={kind.name} caption={kind.hint}>
        <div className="p-5 space-y-5">
          <div className="flex gap-2 flex-wrap">
            {KINDS.map((k) => (
              <button key={k.id} type="button" onClick={() => set('kind', k.id)} className={`px-4 py-2 rounded-lg text-[14px] font-semibold border ${f.kind === k.id ? 'bg-cyan-600 border-cyan-500 text-white' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
                {k.name}
              </button>
            ))}
          </div>
          <RoutePicker passage={passage} onChange={setPassage} mode={pick} onMode={setPick} basemap={basemap} onBasemap={setBasemap} seamarks={seamarks} onSeamarks={setSeamarks} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field name="Start latitude"><input className={input} value={f.startLat} onChange={(e) => set('startLat', e.target.value)} /></Field>
            <Field name="Start longitude"><input className={input} value={f.startLon} onChange={(e) => set('startLon', e.target.value)} /></Field>
            <Field name="End latitude"><input className={input} value={f.endLat} onChange={(e) => set('endLat', e.target.value)} /></Field>
            <Field name="End longitude"><input className={input} value={f.endLon} onChange={(e) => set('endLon', e.target.value)} /></Field>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-[13px] text-slate-400">
            {PRESETS.map((pr) => (
              <button key={pr.id} type="button" className="px-3 py-1.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 font-semibold" onClick={() => setPassage({ start: pr.start, end: pr.end, via: [] })}>
                Preset: {pr.label}
              </button>
            ))}
            <span>{PRESET.note}</span>
          </div>
          <RoutingBoat polar={polar} onPolar={setPolar} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field name={f.kind === 'window' ? 'First departure (your time)' : 'Departure (your time)'}>
              <input type="datetime-local" className={input} value={f.depart} onChange={(e) => set('depart', e.target.value)} />
            </Field>
            {f.kind === 'window' && (
              <>
                <Field name="Over the next">
                  <select className={input} value={f.windowDays} onChange={(e) => set('windowDays', Number(e.target.value))}>
                    {[1, 2, 3, 5, 7].map((d) => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
                  </select>
                </Field>
                <Field name="Every">
                  <select className={input} value={f.intervalH} onChange={(e) => set('intervalH', Number(e.target.value))}>
                    {[3, 6, 12].map((h) => <option key={h} value={h}>{h} hours</option>)}
                  </select>
                </Field>
              </>
            )}
            <Field name="Detail">
              <select className={input} value={f.resolution} onChange={(e) => set('resolution', e.target.value as FormState['resolution'])}>
                <option value="quick">Quick</option>
                <option value="standard">Standard</option>
                <option value="fine">Fine (slow)</option>
              </select>
            </Field>
          </div>
          <div>
            <span className={label}>Models {f.kind === 'route' && <span className="font-normal text-slate-400">(a single route uses the first ticked)</span>}</span>
            <div className="flex gap-4 flex-wrap">
              {MODELS.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-[14px] text-slate-200">
                  <input type="checkbox" checked={f.models[m.id]} onChange={(e) => set('models', { ...f.models, [m.id]: e.target.checked })} />
                  {m.name} <span className="text-[13px] text-slate-400">({m.reach})</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field name="Maximum wind (kn)"><input className={input} value={f.maxTws} onChange={(e) => set('maxTws', e.target.value)} /></Field>
            <Field name="Maximum waves (m, blank = none)"><input className={input} value={f.maxHs} onChange={(e) => set('maxHs', e.target.value)} /></Field>
            <Field name="Maximum gust (kn, blank = none)"><input className={input} value={f.maxGust} onChange={(e) => set('maxGust', e.target.value)} /></Field>
            <Field name="Motor at (kn)"><input className={input} value={f.motoringKt} disabled={!f.motoring} onChange={(e) => set('motoringKt', e.target.value)} /></Field>
          </div>
          <div className="flex gap-6 flex-wrap text-[14px] text-slate-200">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.motoring} onChange={(e) => set('motoring', e.target.checked)} /> Motor when the wind is too light</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.currents} onChange={(e) => set('currents', e.target.checked)} /> Use forecast currents (ECMWF’s, shared with the others)</label>
          </div>
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[240px]"><Field name="Name (optional)"><input className={input} placeholder="e.g. Nouméa → Opua, three models" value={f.label} onChange={(e) => set('label', e.target.value)} /></Field></div>
            <button type="button" onClick={submit} disabled={busy} className="px-6 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-[15px] font-semibold">
              {busy ? 'Sending…' : 'Run'}
            </button>
          </div>
          {error && <p className="text-[14px] text-red-300">{error}</p>}
        </div>
      </Card>

      {selected && (
        <Card
          title="Result"
          caption={KIND_NAME[result?.kind ?? f.kind] ?? undefined}
          action={status && status.state !== 'done' && status.state !== 'failed' ? <ArrowPathIcon className="w-5 h-5 text-cyan-300 animate-spin" /> : undefined}
        >
          <div className="p-5 space-y-3">
            {status && status.state !== 'done' && (
              <p className="text-[14px] text-slate-300">
                <Pill tone={JOB_TONE[status.state]}>{status.state}</Pill>{' '}
                {status.state === 'queued' ? `${status.ahead ?? 0} job(s) ahead` : status.state === 'running' ? status.stage : status.error}
                {status.state !== 'failed' && ' — the first run after a quiet spell also starts the engine (up to a minute or two).'}
              </p>
            )}
            {result && <ResultView r={result} />}
          </div>
        </Card>
      )}

    </div>
    </BasemapContext.Provider>
  );
}
